import { ApolloLink, Observable } from 'apollo-link';
import {
  hasDirectives,
  getMainDefinition,
  checkDocument,
  removeDirectivesFromDocument,
  getDirectivesFromDocument,
  addTypenameToDocument,
} from 'apollo-utilities';
import gql from 'graphql-tag';
import { graphql } from 'graphql-anywhere/lib/async';
import capitalize from 'lodash/capitalize';

const directiveName = 'fc';

const connectionRemoveConfig = {
  test: (directive) => directive.name.value === directiveName,
  remove: true,
};

const removed = new Map();

function removeClientSetsFromDocument(query) {
  // caching
  const cached = removed.get(query);
  /* istanbul ignore if */
  if (cached) return cached;

  checkDocument(query);

  const docClone = removeDirectivesFromDocument([connectionRemoveConfig], query);

  // caching
  removed.set(query, docClone);
  return docClone;
}

const clientQuery = new Map();

function getClientSetsFromDocument(query) {
  // caching
  const cached = clientQuery.get(query);
  /* istanbul ignore if */
  if (cached) return cached;

  const docClone = getDirectivesFromDocument([{ name: directiveName }], query);

  // caching
  clientQuery.set(query, docClone);
  return docClone;
}

export function withFcLink({ service, ...rest }) {
  const resolvers = {
    Query: {
      navianceInfo: () =>
        service.getUserInfo().then(({ academics, demographics, ...rest }) => {
          return {
            ...rest,
            ...academics,
            ...demographics,
            __typename: 'NavianceUserInfo',
          };
        }),
    },
  };

  return new FcLink({ resolvers, ...rest });
}

class FcLink extends ApolloLink {
  constructor({ resolvers, typeDefs }) {
    super();
    this.resolvers = resolvers;
    this.typeDefs = typeDefs;
    this.typeDefsInitialized = false;
  }

  initializeTypeDefs(operation) {
    const { typeDefs, typeDefsInitialized } = this;
    if (typeDefs && !typeDefsInitialized) {
      const directives = `directive @${directiveName} on FIELD`;
      const definition = typeof typeDefs === 'string' ? typeDefs : typeDefs.map((typeDef) => typeDef.trim()).join('\n');

      operation.setContext(({ schemas = [] }) => ({
        schemas: schemas.concat([{ definition, directives }]),
      }));

      this.typeDefsInitialized = true;
    }
  }

  request(operation, forward) {
    this.initializeTypeDefs(operation);

    const resolvers = this.resolvers;
    const { query: originalQuery, variables } = operation;
    const isFcQuery = hasDirectives([directiveName], originalQuery);
    if (!isFcQuery) {
      return forward(operation);
    }

    const nextQuery = removeClientSetsFromDocument(originalQuery);
    const fcQuery = addTypenameToDocument(getClientSetsFromDocument(originalQuery));

    /* istanbul ignore next */
    const operationType = (getMainDefinition(fcQuery) || {}).operation || 'query';
    const type = capitalize(operationType);
    const resolverMap = resolvers[type];

    /* istanbul ignore next */
    const resolver = (fieldName, rootValue = {}, args, context, info) => {
      if (resolverMap) {
        const resolve = resolverMap[fieldName];
        if (resolve) {
          return resolve(args);
        }
      }
      return rootValue && rootValue[fieldName];
    };

    return new Observable((observer) => {
      if (nextQuery) operation.query = nextQuery;
      const obs = nextQuery && forward ? forward(operation) : Observable.of({ data: {} });

      const observerErrorHandler = observer.error.bind(observer);

      const sub = obs.subscribe({
        next: ({ data, errors }) => {
          const context = { ...operation.getContext(), operationType };

          graphql(resolver, fcQuery, data, context, variables)
            .then((nextData) => {
              observer.next({
                data: { ...nextData, ...data },
                errors,
              });
              observer.complete();
            })
            .catch(observerErrorHandler);
        },
        error: observerErrorHandler,
      });

      return () => {
        /* istanbul ignore else */
        if (sub) sub.unsubscribe();
      };
    });
  }
}

export const hasFcLinkQuery = gql`
  query {
    hasFcLink @client
  }
`;
